import 'isomorphic-fetch';
import fetchCheck from '../redux/middleware/fetchCheck';
import { addNotification } from './notificationActions';
import * as types from './actionTypes';
import Notifications from '../containers/Pages/MultiMediaWizard/utils/Notifications';

/**
 * @typedef {import('../containers/Pages/TagManager/types').TagRow} TagRow
 * @typedef {import('../containers/Pages/TagManager/types').TagUpdates} TagUpdates
 * @typedef {import('../containers/Pages/TagManager/types').TagConfig} TagConfig
 */

export const url = '/cms/data/v1/tagmanager/';

export const configUrl = '/cms/data/v1/tagmanager/config';

export const credentials = 'include';

export const headers = { Accept: 'application/json', 'Content-Type': 'application/json' };

/**
 * Creates the request action for the active tags fetch.
 *
 * @returns {Object} The TAG_MANAGER_TAGS_REQUEST action.
 */
export const tagsRequest = () => ({ type: types.TAG_MANAGER_TAGS_REQUEST });

/**
 * Creates the success action for the active tags fetch.
 *
 * @param {TagRow[]} result - The active tag rows.
 * @returns {Object} The TAG_MANAGER_TAGS_REQUEST_SUCCESS action.
 */
export const tagsRequestSuccess = result => ({
  type: types.TAG_MANAGER_TAGS_REQUEST_SUCCESS,
  result,
});

/**
 * Creates the failure action for the active tags fetch.
 *
 * @param {Object} error - The request error.
 * @returns {Object} The TAG_MANAGER_TAGS_REQUEST_FAILED action.
 */
export const tagsRequestFailed = error => ({
  type: types.TAG_MANAGER_TAGS_REQUEST_FAILED,
  error,
});

/**
 * Creates the request action for the config fetch.
 *
 * @returns {Object} The TAG_MANAGER_CONFIG_REQUEST action.
 */
export const configRequest = () => ({ type: types.TAG_MANAGER_CONFIG_REQUEST });

/**
 * Creates the success action for the config fetch.
 *
 * @param {boolean} result - The normalized enabled flag.
 * @returns {Object} The TAG_MANAGER_CONFIG_REQUEST_SUCCESS action.
 */
export const configRequestSuccess = result => ({
  type: types.TAG_MANAGER_CONFIG_REQUEST_SUCCESS,
  result,
});

/**
 * Creates the failure action for the config fetch.
 *
 * @param {Object} error - The request error.
 * @returns {Object} The TAG_MANAGER_CONFIG_REQUEST_FAILED action.
 */
export const configRequestFailed = error => ({
  type: types.TAG_MANAGER_CONFIG_REQUEST_FAILED,
  error,
});

/**
 * Fetches the active tag rows for the current client.
 *
 * @returns {Promise<TagRow[]>} A promise resolving to the active tag rows.
 */
export const fetchTags = () =>
  fetch(url, { method: 'GET', credentials, headers })
    .then(res => fetchCheck(res))
    .then(res => res.json());

/**
 * Fetches the Tag Manager feature flag for the current client.
 *
 * @returns {Promise<TagConfig>} A promise resolving to `{ enabled }`.
 */
export const fetchTagConfig = () =>
  fetch(configUrl, { method: 'GET', credentials, headers })
    .then(res => fetchCheck(res))
    .then(res => res.json());

/**
 * Validates and normalizes the config response to a boolean. Only boolean
 * true/false and numeric 1/0 are accepted; the database column is TINYINT so
 * generic truthiness would incorrectly treat a string value such as '0' as
 * enabled.
 *
 * @param {TagConfig} config - The parsed config response, expected
 *     `{ enabled }`.
 * @returns {boolean} The normalized enabled flag.
 * @throws {Error} When the enabled value is missing or unsupported.
 */
export const normalizeConfig = config => {
  const { enabled } = config || {};

  if (enabled === true || enabled === false || enabled === 1 || enabled === 0) {
    return Boolean(enabled);
  }

  throw new Error('Unsupported Tag Manager config value received.');
};

/**
 * Loads the active tags, dispatching request, success, and failure actions.
 * On failure an application notification is shown and the error is kept in
 * Redux.
 *
 * @param {function(Object): void} dispatch - The Redux dispatch function.
 * @returns {Promise} A promise resolving when the request settles.
 */
export const getTags = () => dispatch => {
  dispatch(tagsRequest());

  return fetchTags().then(
    res => dispatch(tagsRequestSuccess(res)),
    err => {
      dispatch(tagsRequestFailed(err));
      dispatch(
        addNotification({
          type: 'danger',
          text: err?.userMessage || 'Unable to load the active tags.',
        }),
      );
    },
  );
};

/**
 * Loads the Tag Manager feature flag, dispatching request, success, and
 * failure actions. The response is validated and normalized to a boolean
 * before the success action is dispatched. On failure an application
 * notification is shown and the error is kept in Redux.
 *
 * @param {function(Object): void} dispatch - The Redux dispatch function.
 * @returns {Promise} A promise resolving when the request settles.
 */
export const getTagConfig = () => dispatch => {
  dispatch(configRequest());

  return fetchTagConfig()
    .then(normalizeConfig)
    .then(
      res => dispatch(configRequestSuccess(res)),
      err => {
        dispatch(configRequestFailed(err));
        dispatch(
          addNotification({
            type: 'danger',
            text:
              err?.userMessage ||
              (err instanceof Error && err.message) ||
              'Unable to load the Tag Manager setting.',
          }),
        );
      },
    );
};

/**
 * Fetches all tag rows including archived ones for the current client.
 *
 * @returns {Promise<TagRow[]>} A promise resolving to all tag rows.
 */
export const fetchAllTags = () =>
  fetch(`${url}all`, { method: 'GET', credentials, headers })
    .then(res => fetchCheck(res))
    .then(res => res.json());

/**
 * Creates the request action for the all-tags fetch.
 *
 * @returns {Object} The TAG_MANAGER_ALL_TAGS_REQUEST action.
 */
export const allTagsRequest = () => ({ type: types.TAG_MANAGER_ALL_TAGS_REQUEST });

/**
 * Creates the success action for the all-tags fetch.
 *
 * @param {TagRow[]} result - All tag rows.
 * @returns {Object} The TAG_MANAGER_ALL_TAGS_REQUEST_SUCCESS action.
 */
export const allTagsRequestSuccess = result => ({
  type: types.TAG_MANAGER_ALL_TAGS_REQUEST_SUCCESS,
  result,
});

/**
 * Creates the failure action for the all-tags fetch.
 *
 * @param {Object} error - The request error.
 * @returns {Object} The TAG_MANAGER_ALL_TAGS_REQUEST_FAILED action.
 */
export const allTagsRequestFailed = error => ({
  type: types.TAG_MANAGER_ALL_TAGS_REQUEST_FAILED,
  error,
});

/**
 * Creates the request action for the config update.
 *
 * @returns {Object} The TAG_MANAGER_CONFIG_SET_REQUEST action.
 */
export const configSetRequest = () => ({ type: types.TAG_MANAGER_CONFIG_SET_REQUEST });

/**
 * Creates the success action for the config update.
 *
 * @param {boolean} result - The normalized enabled flag.
 * @returns {Object} The TAG_MANAGER_CONFIG_SET_REQUEST_SUCCESS action.
 */
export const configSetRequestSuccess = result => ({
  type: types.TAG_MANAGER_CONFIG_SET_REQUEST_SUCCESS,
  result,
});

/**
 * Creates the failure action for the config update.
 *
 * @param {Object} error - The request error.
 * @returns {Object} The TAG_MANAGER_CONFIG_SET_REQUEST_FAILED action.
 */
export const configSetRequestFailed = error => ({
  type: types.TAG_MANAGER_CONFIG_SET_REQUEST_FAILED,
  error,
});

/**
 * Loads all tags (including archived), dispatching request, success, and
 * failure actions. On failure an application notification is shown and the
 * error is kept in Redux.
 *
 * @param {function(Object): void} dispatch - The Redux dispatch function.
 * @returns {Promise} A promise resolving when the request settles.
 */
export const getAllTags = () => dispatch => {
  dispatch(allTagsRequest());

  return fetchAllTags().then(
    res => dispatch(allTagsRequestSuccess(res)),
    err => {
      dispatch(allTagsRequestFailed(err));
      dispatch(
        addNotification({
          type: 'danger',
          text: err?.userMessage || 'Unable to load all tags.',
        }),
      );
    },
  );
};

/**
 * Posts an updated enabled value to the Tag Manager config endpoint. The
 * response is validated and normalized to a boolean before the success action
 * is dispatched. On success a notification reports the new state. On failure
 * an application notification is shown and the error is kept in Redux.
 *
 * @param {boolean} enabled - The new enabled value.
 * @param {function(Object): void} dispatch - The Redux dispatch function.
 * @returns {Promise} A promise resolving when the request settles.
 */
export const setTagConfigEnabled = enabled => dispatch => {
  dispatch(configSetRequest());

  return fetch(configUrl, {
    method: 'POST',
    credentials,
    headers,
    body: JSON.stringify({ enabled }),
  })
    .then(res => fetchCheck(res))
    .then(res => res.json())
    .then(normalizeConfig)
    .then(
      res => {
        dispatch(configSetRequestSuccess(res));
        Notifications.success(res ? 'Tag Manager enabled.' : 'Tag Manager disabled.');
      },
      err => {
        dispatch(configSetRequestFailed(err));
        dispatch(
          addNotification({
            type: 'danger',
            text:
              err?.userMessage ||
              (err instanceof Error && err.message) ||
              'Unable to update the Tag Manager setting.',
          }),
        );
      },
    );
};

/**
 * Creates the request action for the tag create.
 *
 * @returns {Object} The TAG_MANAGER_TAG_CREATE_REQUEST action.
 */
export const tagCreateRequest = () => ({ type: types.TAG_MANAGER_TAG_CREATE_REQUEST });

/**
 * Creates the success action for the tag create.
 *
 * @param {TagRow} result - The created tag row.
 * @returns {Object} The TAG_MANAGER_TAG_CREATE_REQUEST_SUCCESS action.
 */
export const tagCreateRequestSuccess = result => ({
  type: types.TAG_MANAGER_TAG_CREATE_REQUEST_SUCCESS,
  result,
});

/**
 * Creates the failure action for the tag create.
 *
 * @param {Object} error - The request error.
 * @returns {Object} The TAG_MANAGER_TAG_CREATE_REQUEST_FAILED action.
 */
export const tagCreateRequestFailed = error => ({
  type: types.TAG_MANAGER_TAG_CREATE_REQUEST_FAILED,
  error,
});

/**
 * Creates the request action for the tag update.
 *
 * @returns {Object} The TAG_MANAGER_TAG_UPDATE_REQUEST action.
 */
export const tagUpdateRequest = () => ({ type: types.TAG_MANAGER_TAG_UPDATE_REQUEST });

/**
 * Creates the success action for the tag update.
 *
 * @param {TagRow} result - The updated tag row.
 * @returns {Object} The TAG_MANAGER_TAG_UPDATE_REQUEST_SUCCESS action.
 */
export const tagUpdateRequestSuccess = result => ({
  type: types.TAG_MANAGER_TAG_UPDATE_REQUEST_SUCCESS,
  result,
});

/**
 * Creates the failure action for the tag update.
 *
 * @param {Object} error - The request error.
 * @returns {Object} The TAG_MANAGER_TAG_UPDATE_REQUEST_FAILED action.
 */
export const tagUpdateRequestFailed = error => ({
  type: types.TAG_MANAGER_TAG_UPDATE_REQUEST_FAILED,
  error,
});

/**
 * Creates the request action for the tag delete.
 *
 * @returns {Object} The TAG_MANAGER_TAG_DELETE_REQUEST action.
 */
export const tagDeleteRequest = () => ({ type: types.TAG_MANAGER_TAG_DELETE_REQUEST });

/**
 * Creates the success action for the tag delete.
 *
 * @param {Object} result - The delete status.
 * @returns {Object} The TAG_MANAGER_TAG_DELETE_REQUEST_SUCCESS action.
 */
export const tagDeleteRequestSuccess = result => ({
  type: types.TAG_MANAGER_TAG_DELETE_REQUEST_SUCCESS,
  result,
});

/**
 * Creates the failure action for the tag delete.
 *
 * @param {Object} error - The request error.
 * @returns {Object} The TAG_MANAGER_TAG_DELETE_REQUEST_FAILED action.
 */
export const tagDeleteRequestFailed = error => ({
  type: types.TAG_MANAGER_TAG_DELETE_REQUEST_FAILED,
  error,
});

/**
 * Creates a tag via POST /tagmanager. The API ignores duplicate labels and
 * returns the existing row, so the resolved row may be pre-existing.
 *
 * @param {string} tag - The tag label to create.
 * @returns {Promise<TagRow>} A promise resolving to the created tag row.
 */
export const fetchCreateTag = tag =>
  fetch(url, { method: 'POST', credentials, headers, body: JSON.stringify({ tag }) })
    .then(res => fetchCheck(res))
    .then(res => res.json());

/**
 * Updates a tag via PUT /tagmanager/:tagId. Only the supplied fields change.
 *
 * @param {number|string} tagId - The id of the tag to update.
 * @param {TagUpdates} updates - The fields to update (`tag`, `archived`).
 * @returns {Promise<TagRow>} A promise resolving to the updated tag row.
 */
export const fetchUpdateTag = (tagId, updates) =>
  fetch(`${url}${tagId}`, {
    method: 'PUT',
    credentials,
    headers,
    body: JSON.stringify(updates),
  })
    .then(res => fetchCheck(res))
    .then(res => res.json());

/**
 * Deletes a tag via DELETE /tagmanager/:tagId.
 *
 * @param {number|string} tagId - The id of the tag to delete.
 * @returns {Promise<Object>} A promise resolving to the delete status.
 */
export const fetchDeleteTag = tagId =>
  fetch(`${url}${tagId}`, { method: 'DELETE', credentials, headers })
    .then(res => fetchCheck(res))
    .then(res => res.json());

/**
 * Creates a tag, dispatching request, success, and failure actions. On
 * success the returned row is stored, a notification reports the creation,
 * and the full tag lists are refreshed so both the admin table and the
 * TagCloud options stay current. On failure an application notification is
 * shown and the error is kept in Redux.
 *
 * @param {string} tag - The tag label to create.
 * @param {function(Object): void} dispatch - The Redux dispatch function.
 * @returns {Promise} A promise resolving when the request settles.
 */
export const createTag = tag => dispatch => {
  dispatch(tagCreateRequest());

  return fetchCreateTag(tag).then(
    res => {
      dispatch(tagCreateRequestSuccess(res));
      Notifications.success(`Tag "${tag}" created.`);

      return Promise.all([dispatch(getTags()), dispatch(getAllTags())]);
    },
    err => {
      dispatch(tagCreateRequestFailed(err));
      dispatch(
        addNotification({
          type: 'danger',
          text: err?.userMessage || 'Unable to create the tag.',
        }),
      );
    },
  );
};

/**
 * Updates a tag (rename or archive toggle), dispatching request, success, and
 * failure actions. On success the returned row is stored, a notification
 * reports the rename or archive change, and the full tag lists are refreshed.
 * On failure an application notification is shown and the error is kept in
 * Redux.
 *
 * @param {number|string} tagId - The id of the tag to update.
 * @param {string} name - The current tag label, used in notifications.
 * @param {TagUpdates} updates - The fields to update (`tag`, `archived`).
 * @param {function(Object): void} dispatch - The Redux dispatch function.
 * @returns {Promise} A promise resolving when the request settles.
 */
export const updateTag = (tagId, name, updates) => dispatch => {
  dispatch(tagUpdateRequest());

  return fetchUpdateTag(tagId, updates).then(
    res => {
      dispatch(tagUpdateRequestSuccess(res));

      if (updates.tag) {
        Notifications.success(`Tag '${name}' renamed to '${updates.tag}'.`);
      } else if (updates.archived !== undefined) {
        Notifications.success(`Tag '${name}' ${updates.archived ? 'archived.' : 'unarchived.'}`);
      }

      return Promise.all([dispatch(getTags()), dispatch(getAllTags())]);
    },
    err => {
      dispatch(tagUpdateRequestFailed(err));
      dispatch(
        addNotification({
          type: 'danger',
          text: err?.userMessage || 'Unable to update the tag.',
        }),
      );
    },
  );
};

/**
 * Deletes a tag, dispatching request, success, and failure actions. On
 * success a notification reports the deletion and the full tag lists are
 * refreshed. On failure an application notification is shown and the error is
 * kept in Redux.
 *
 * @param {number|string} tagId - The id of the tag to delete.
 * @param {string} name - The tag label, used in notifications.
 * @param {function(Object): void} dispatch - The Redux dispatch function.
 * @returns {Promise} A promise resolving when the request settles.
 */
export const deleteTag =
  (tagId, name = null) =>
  dispatch => {
    dispatch(tagDeleteRequest());

    return fetchDeleteTag(tagId).then(
      res => {
        dispatch(tagDeleteRequestSuccess(res));
        Notifications.success(`Tag '${name}' deleted.`);

        return Promise.all([dispatch(getTags()), dispatch(getAllTags())]);
      },
      err => {
        dispatch(tagDeleteRequestFailed(err));
        dispatch(
          addNotification({
            type: 'danger',
            text: err?.userMessage || 'Unable to delete the tag.',
          }),
        );
      },
    );
  };

/**
 * Orchestration thunk that starts both independent Tag Manager requests in
 * parallel. Each request dispatches its own success or failure actions.
 *
 * @param {function(Object): void} dispatch - The Redux dispatch function.
 * @returns {Promise} A promise resolving when both requests have settled.
 */
export const getTagManager = () => dispatch =>
  Promise.all([dispatch(getTags()), dispatch(getTagConfig())]);
